import React, { useState } from "react";
import { CaseLink } from "@/components/CaseLink";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertOctagon, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Info } from "@/components/Info";

const REASONS = ["Bank delay", "Customer delay", "Documents pending from builder/seller", "Valuation/legal visit pending", "Other"];
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const unitLabel = (t) => (t.unit === "working" ? " working" : "");

export function stuckLabel(t) {
  return `Stuck · ${t.elapsed}${unitLabel(t)} days in ${t.stage} (limit ${t.limit})`;
}

export function StuckBadge({ tat, caseId, onExtend, compact }) {
  if (!tat) return null;
  if (tat.unknown_start) {
    return (
      <span className={`inline-flex items-center gap-1.5 rounded-full bg-[#F5B71D]/15 text-[#92600a] border border-[#F5B71D]/40 ${compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"}`} data-testid={`stage-start-unknown-${caseId}`}>
        <Clock className="w-3.5 h-3.5" /> Stage start date unknown
      </span>
    );
  }
  if (tat.stuck) {
    return (
      <span className={`inline-flex items-center gap-1.5 rounded-full bg-[#CA1212]/10 text-[#CA1212] border border-[#CA1212]/30 font-medium ${compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"}`} data-testid={`stuck-badge-${caseId}`}>
        <AlertOctagon className="w-3.5 h-3.5" /> {stuckLabel(tat)}{tat.set_bank_type ? " · Set Bank Type" : ""}
        {!compact && <Info k="stuck" />}
        {onExtend && <button onClick={onExtend} data-testid={`stuck-extend-${caseId}`} className="ml-1 underline">Extend</button>}
      </span>
    );
  }
  if (tat.extended_until) {
    return (
      <span className={`inline-flex items-center gap-1.5 rounded-full bg-[#F5B71D]/15 text-[#92600a] border border-[#F5B71D]/40 ${compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"}`} data-testid={`extended-badge-${caseId}`}>
        <Clock className="w-3.5 h-3.5" /> Extended to {tat.extended_until} · {tat.ext_reason}
      </span>
    );
  }
  return null;
}

export function ExtendDialog({ recordId, tat, onClose, onDone }) {
  const [until, setUntil] = useState(addDays(2));
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [fu, setFu] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason) { toast.error("Pick a reason"); return; }
    setSaving(true);
    try {
      await api.post(`/cases/${recordId}/tat/extend`, { until, reason, note: note || null, next_action: nextAction || null, follow_up: fu || null });
      toast.success(`Extended to ${until}`); onDone?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not extend"); }
    finally { setSaving(false); }
  };
  const pill = (on) => `px-2.5 py-1 rounded-full text-xs border ${on ? "bg-[#FF4802] text-white border-[#FF4802]" : "border-[#E2E2E2] text-[#6B7280] hover:border-[#FF6F38]"}`;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white rounded-2xl truva-popup-shadow max-w-md" aria-describedby={undefined} data-testid="extend-dialog">
        <DialogHeader><DialogTitle className="text-[#333333] font-medium">Extend {tat?.stage} TAT</DialogTitle></DialogHeader>
        <p className="text-xs text-[#6B7280]">{stuckLabel(tat)}. Extensions used in this stage: {tat?.extensions || 0}/2.</p>
        <div className="flex flex-wrap gap-1.5 items-center">
          {[["+2 days", 2], ["+3 days", 3], ["+1 week", 7]].map(([l, d]) => <button key={l} onClick={() => setUntil(addDays(d))} data-testid={`extend-${d}`} className={pill(until === addDays(d))}>{l}</button>)}
          <input type="date" value={until} min={addDays(1)} onChange={(e) => setUntil(e.target.value)} data-testid="extend-date" className="text-xs border border-[#E2E2E2] rounded-md px-2 py-1" />
        </div>
        <div className="flex flex-wrap gap-1.5">{REASONS.map((r) => <button key={r} onClick={() => setReason(r)} data-testid={`extend-reason-${r.split(" ")[0].toLowerCase()}`} className={pill(reason === r)}>{r}</button>)}</div>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={reason === "Other" ? "Note (required for Other)" : "Note (optional)"} data-testid="extend-note" className="border-[#E2E2E2] rounded-lg text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <Input value={nextAction} onChange={(e) => setNextAction(e.target.value)} placeholder="Set Next Action (optional)" data-testid="extend-next-action" className="border-[#E2E2E2] rounded-lg text-sm" />
          <input type="date" value={fu} onChange={(e) => setFu(e.target.value)} data-testid="extend-follow-up" className="text-sm border border-[#E2E2E2] rounded-lg px-2" title="Set Follow-up (optional)" />
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[#E2E2E2] text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="extend-confirm" className="px-4 py-2 rounded-lg bg-[#FF4802] text-white text-sm font-medium disabled:opacity-50">{saving ? "Saving…" : "Extend"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StuckList({ rows, navigate, testid, showExtended }) {
  if (!rows?.length) return <p className="text-sm text-[#6B7280]">{showExtended ? "No extended cases" : "No stuck cases"}</p>;
  return (
    <div className="rounded-2xl border border-[#E2E2E2] bg-white shadow-sm overflow-hidden" data-testid={testid}>
      {rows.map((r) => (
        <div key={r.record_id} className="flex items-center justify-between px-3.5 py-2.5 border-b border-[#F7F7F7] last:border-0 hover:bg-[#F6F5EF]/70 cursor-pointer" onClick={() => navigate(`/cases/${r.record_id}`)} data-testid={`stuck-row-${r.case_id}`}>
          <div className="min-w-0"><CaseLink recordId={r.record_id} className="font-mono font-semibold text-[#E2521B] text-xs hover:underline">{r.case_id}</CaseLink><span className="text-sm text-[#333333] ml-2">{r.borrower || r.name}</span>
            {r.hl_rm && <span className="text-[11px] text-[#6B7280] ml-2">· {Array.isArray(r.hl_rm) ? r.hl_rm.join(", ") : r.hl_rm}</span>}</div>
          <StuckBadge tat={r.tat} caseId={r.case_id} compact />
        </div>
      ))}
    </div>
  );
}
